/**
 * ルーティングの薄いラッパ。
 *
 * TanStack Router の `Link` / `useNavigate` は「ルート木を型登録した場合」に
 * パス文字列をリテラル型で検査する。本アプリはルート木を型登録していない
 * （`declare module '@tanstack/react-router'` を行っていない）ため、
 * 型の緩和はこのファイルの中だけで完結させ、画面側には素直な
 * `to: string` のインタフェースだけを見せる。
 *
 * 将来ルート木を型登録するときは、このファイルの中身を差し替えるだけで済む。
 */
import * as React from 'react';
import { Link, redirect, useNavigate, useParams, useRouterState, useSearch } from '@tanstack/react-router';

export interface AppLinkProps {
  to: string;
  className?: string;
  children?: React.ReactNode;
  target?: string;
  rel?: string;
  title?: string;
  'aria-label'?: string;
  onClick?: (e: React.MouseEvent) => void;
  replace?: boolean;
}

const RawLink = Link as unknown as React.ComponentType<
  AppLinkProps & { ref?: React.Ref<HTMLAnchorElement> }
>;

/**
 * `<Button asChild>` の中に置けるよう forwardRef にしておく。
 * Radix の Slot は必ず ref を子へ渡すので、素の関数コンポーネントだと
 * 「Function components cannot be given refs」の警告が出る。
 */
export const AppLink = React.forwardRef<HTMLAnchorElement, AppLinkProps>(function AppLink(
  props,
  ref,
) {
  return <RawLink {...props} ref={ref} />;
});

export interface NavigateOptions {
  to: string;
  replace?: boolean;
  search?: Record<string, unknown>;
}

export type NavigateFn = (options: NavigateOptions) => void;

export function useAppNavigate(): NavigateFn {
  const navigate = useNavigate();
  return navigate as unknown as NavigateFn;
}

/** 現在のパス。AppShell のアクティブ判定に渡す。 */
export function useCurrentPath(): string {
  return useRouterState({ select: (s) => s.location.pathname });
}

/**
 * パスパラメータを取り出す。
 * ルート木を型登録していないので `strict: false` で全ルート分をまとめて受け、
 * 呼び出し側が必要なキーだけを読む。
 */
export function useRouteParams(): Record<string, string | undefined> {
  return useParams({ strict: false }) as Record<string, string | undefined>;
}

/** クエリ文字列。ログイン後の戻り先（`?redirect=`）などに使う。 */
export function useSearchParams(): Record<string, unknown> {
  return useSearch({ strict: false }) as Record<string, unknown>;
}

const rawRedirect = redirect as unknown as (opts: {
  to: string;
  search?: Record<string, unknown>;
  replace?: boolean;
}) => Error;

/** `beforeLoad` から投げるリダイレクト。 */
export function redirectTo(to: string, search?: Record<string, unknown>): never {
  throw rawRedirect({ to, search: search ?? {}, replace: true });
}
